import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardBody,
  FormGroup,
  Button,
  Container,
  Spinner,
} from "reactstrap";
import { AvForm, AvField } from "availity-reactstrap-validation";
//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { CreateLocation, SearchCities } from "../../apis/PopularLocation";

const AddLocation = () => {
  const [selectedCity, setSelectedCity] = useState(null); // { en, ar }
  const [cityInput, setCityInput] = useState("");
  const [cityOptions, setCityOptions] = useState([]);
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [isCityLoading, setIsCityLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [status] = useState("active");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  const breadcrumbItems = [
    { title: "Dashboard", link: "/" },
    { title: "Location", link: "#" },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsCityDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch all cities on mount for initial dropdown
  useEffect(() => {
    setIsCityLoading(true);
    SearchCities("")
      .then((hits) => setCityOptions(hits || []))
      .catch(() => {})
      .finally(() => setIsCityLoading(false));
  }, []);

  const handleCityInputChange = (e) => {
    const value = e.target.value;
    setCityInput(value);
    setSelectedCity(null);

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setIsCityLoading(true);
      SearchCities(value)
        .then((hits) => setCityOptions(hits || []))
        .catch(() => {})
        .finally(() => setIsCityLoading(false));
    }, 300);
  };

  const handleCitySelect = (city) => {
    setSelectedCity({ en: city.nameEn, ar: city.nameAr });
    setCityInput(city.nameEn);
    setIsCityDropdownOpen(false);
  };

  const handleImageChange = (e) => {
    setImage(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCity) {
      toast.error("Please select a city from the suggestions");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await CreateLocation(image, selectedCity, status);
      if (response) {
        toast.success("Location Created Successfully");
        navigate("/popular-location");
      } else {
        toast.error("Location Creation Failed");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container>
          <Breadcrumbs title="Add space" breadcrumbItems={breadcrumbItems} />
          <Card>
            <CardBody>
              <h4 className="card-title">Add location</h4>
              <AvForm onValidSubmit={handleSubmit}>
                {/* City autocomplete */}
                <div
                  className="mb-3"
                  ref={dropdownRef}
                  style={{ position: "relative" }}
                >
                  <label className="form-label">City</label>
                  <div style={{ position: "relative" }}>
                    <input
                      className="form-control"
                      type="text"
                      placeholder="Search for a city..."
                      value={cityInput}
                      onChange={handleCityInputChange}
                      onFocus={() => setIsCityDropdownOpen(true)}
                      autoComplete="off"
                    />
                    {isCityLoading && (
                      <span
                        style={{
                          position: "absolute",
                          right: 10,
                          top: "50%",
                          transform: "translateY(-50%)",
                        }}
                      >
                        <Spinner size="sm" />
                      </span>
                    )}
                  </div>
                  {!selectedCity && cityInput && (
                    <small className="text-danger">
                      Please select a city from the list
                    </small>
                  )}
                  {isCityDropdownOpen && cityOptions.length > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        background: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: 4,
                        maxHeight: 220,
                        overflowY: "auto",
                        zIndex: 1000,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                    >
                      {cityOptions.map((city) => (
                        <div
                          key={city.cityId}
                          onMouseDown={() => handleCitySelect(city)}
                          style={{
                            padding: "8px 12px",
                            cursor: "pointer",
                            borderBottom: "1px solid #f0f0f0",
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = "#f5f5f5")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "#fff")
                          }
                        >
                          <span>{city.nameEn}</span>
                          <span style={{ color: "#888", direction: "rtl" }}>
                            {city.nameAr}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Show selected city names */}
                {selectedCity && (
                  <div className="mb-3">
                    <small className="text-muted">
                      English: <strong>{selectedCity.en}</strong> &nbsp;|&nbsp;
                      Arabic: <strong>{selectedCity.ar}</strong>
                    </small>
                  </div>
                )}

                <div className="mb-3">
                  <AvField
                    name="image"
                    label="Location Image"
                    placeholder="Type Something"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    errorMessage="Upload an image"
                    validate={{ required: { value: true } }}
                  />
                </div>

                <FormGroup className="mb-0">
                  <div>
                    <Button
                      type="submit"
                      color="primary"
                      className="me-1"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner size="sm" className="me-1" /> Saving...
                        </>
                      ) : (
                        "Add"
                      )}
                    </Button>{" "}
                    <Button
                      type="reset"
                      color="secondary"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                  </div>
                </FormGroup>
              </AvForm>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AddLocation;
